import { CartLinesQueryResult, CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import { MeFavoritesProductsQueryResult, MeQueryResult } from '@graphql/queries/__generated__/me.generated'
import { RewardProgramsQuery } from '@graphql/queries/__generated__/reward-programs.generated'
import { Divider } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import { ACCOUNT_LOGIN_URL } from '../../urls'
import CartFavorites from './components/CartFavorites'
import CartLines from './components/CartLines'
import CartLinesTotal from './components/CartLinesTotal'
import { TCartParams, TCartSetParams } from './types'
import { useTranslation } from 'react-i18next'
import { CurrentCurrencyExchangeRateQueryResult } from '@graphql/queries/__generated__/shipping.generated'

interface PageProps {
  isAuthenticated: boolean
  lineLoading: number | null
  cartLinesQueryResult: CartLinesQueryResult
  cartQueryResult: CartQueryResult
  favoritesQueryResult: MeFavoritesProductsQueryResult
  meQueryResult: MeQueryResult
  firstRewardProgram: RewardProgramsQuery['rewardPrograms']['edges'][0] | null
  params: TCartParams
  setParams: TCartSetParams
  currencyExchangeRateQueryResult: CurrentCurrencyExchangeRateQueryResult

  handleSignIn(): void

  handleEditQty(linePk: number, qty: number): Promise<void>

  handleRemoveLine(linePk: number): Promise<void>

  handleSaveForLaterLine(productPk: number, linePk: number): Promise<void>

  handleEditLine(linePk: number): Promise<void>

  onClickProduct(specId: string | number, slug: string): void

  handleRemoveFromFavorites(productPk: number): Promise<void>
}

const Page: React.FC<PageProps> = ({
  handleSignIn,
  cartLinesQueryResult,
  cartQueryResult,
  handleEditQty,
  isAuthenticated,
  handleEditLine,
  handleRemoveLine,
  handleSaveForLaterLine,
  lineLoading,
  onClickProduct,
  favoritesQueryResult,
  handleRemoveFromFavorites,
  params,
  setParams,
  meQueryResult,
  firstRewardProgram,
  currencyExchangeRateQueryResult,
}) => {
  const { t } = useTranslation()
  return (
    <div className='p-0 sm:p-24 flex flex-col md:flex-row justify-center bg-gray-50' style={{ minHeight: 'inherit' }}>
      <div className='w-full md:w-640 sm:mr-24'>
        <div className='bg-white shadow-md relative rounded-md'>
          <div className='flex flex-col md:flex-row md:items-center flex-wrap p-12'>
            <h1 className='font-bold text-lg uppercase mr-12 leading-24'>{t('title.shoppingBag', 'Shopping Bag')}</h1>
            <span className='text-13 text-gray-700 leading-18'>
              {t('itemsWithCount', { defaultValue: '{{count}} Items', count: cartQueryResult.data?.cart?.count || 0 })}
            </span>
            {!isAuthenticated && (
              <span className='ml-auto leading-18 text-13'>
                <span className='text-gray-700 mr-5'>{t('haveAnAccount', 'Have an account?')}</span>
                <Link
                  className='text-blue no-underline'
                  to={ACCOUNT_LOGIN_URL}
                  onClick={e => {
                    e.preventDefault()
                    handleSignIn()
                  }}
                >
                  {t('button.signIn', 'Sign in')}
                </Link>
              </span>
            )}
          </div>
          <Divider />
          <CartLines
            cartLinesQueryResult={cartLinesQueryResult}
            handleEditQty={handleEditQty}
            handleEditLine={handleEditLine}
            handleRemoveLine={handleRemoveLine}
            handleSaveForLaterLine={handleSaveForLaterLine}
            lineLoading={lineLoading}
            onClickProduct={onClickProduct}
            isAuthenticated={isAuthenticated}
            page={params.page || 1}
            setPage={(page: number) => setParams({ page }, 'replaceIn')}
            paginateBy={params.paginateBy || 10}
            setPaginateBy={(paginateBy: number) => setParams({ paginateBy, page: 1 }, 'replaceIn')}
          />
        </div>
        {isAuthenticated && (
          <div className='mt-24'>
            <CartFavorites
              favoritesQueryResult={favoritesQueryResult}
              onClickProduct={onClickProduct}
              favoritesLineLoading={lineLoading}
              handleRemoveFromFavorites={handleRemoveFromFavorites}
              page={params.favoritesPage || 1}
              setPage={(page: number) => setParams({ favoritesPage: page }, 'replaceIn')}
            />
          </div>
        )}
      </div>

      <div className='w-full md:w-320 mt-20 md:mt-0 flex flex-col'>
        <CartLinesTotal
          isAuthenticated={isAuthenticated}
          cartQueryResult={cartQueryResult}
          handleSignIn={handleSignIn}
          loading={!!lineLoading || cartQueryResult.loading}
          meQueryResult={meQueryResult}
          firstRewardProgram={firstRewardProgram}
          currencyExchangeRateQueryResult={currencyExchangeRateQueryResult}
        />
      </div>
    </div>
  )
}

export default Page
